import api from './api';

export const cropService = {
  // =============================================================================
  // CROP GROUPS
  // =============================================================================

  // Get all crop groups with pagination and search
  async getCropGroups(params = {}) {
    try {
      const response = await api.get('/crop-groups', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch crop groups');
    }
  },

  // Get crop group by ID
  async getCropGroup(id) {
    try {
      const response = await api.get(`/crop-groups/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch crop group');
    }
  },

  // Create new crop group (Admin/Supervisor only)
  async createCropGroup(cropGroupData) {
    try {
      const response = await api.post('/crop-groups', cropGroupData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create crop group');
    }
  },

  // Update crop group (Admin/Supervisor only)
  async updateCropGroup(id, cropGroupData) {
    try {
      const response = await api.put(`/crop-groups/${id}`, cropGroupData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update crop group');
    }
  },

  // Delete crop group (Admin only)
  async deleteCropGroup(id) {
    try {
      const response = await api.delete(`/crop-groups/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete crop group');
    }
  },

  // Export crop groups to Excel
  async exportCropGroups() {
    try {
      const response = await api.get('/crop-groups/export/excel', {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export crop groups');
    }
  },

  // =============================================================================
  // INDIVIDUAL CROPS
  // =============================================================================

  // Get all crops with pagination, search, and group filtering
  async getCrops(params = {}) {
    try {
      const response = await api.get('/crops', { params });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch crops');
    }
  },

  // Get crop by ID
  async getCrop(id) {
    try {
      const response = await api.get(`/crops/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch crop');
    }
  },

  // Create new crop (Admin/Supervisor only)
  async createCrop(cropData) {
    try {
      const response = await api.post('/crops', cropData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create crop');
    }
  },

  // Update crop (Admin/Supervisor only)
  async updateCrop(id, cropData) {
    try {
      const response = await api.put(`/crops/${id}`, cropData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update crop');
    }
  },

  // Delete crop (Admin only)
  async deleteCrop(id) {
    try {
      const response = await api.delete(`/crops/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete crop');
    }
  },

  // Get crops by group
  async getCropsByGroup(groupId) {
    try {
      const response = await api.get(`/crops/by-group/${groupId}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch crops by group');
    }
  },

  // Export crops to Excel
  async exportCrops() {
    try {
      const response = await api.get('/crops/export/excel', {
        responseType: 'blob',
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export crops');
    }
  },

  // =============================================================================
  // CROP HIERARCHY
  // =============================================================================

  // Get complete crop hierarchy
  async getCropHierarchy() {
    try {
      const [cropGroups, crops] = await Promise.all([
        this.getCropGroups(),
        this.getCrops()
      ]);

      // Build hierarchy
      const hierarchy = cropGroups.map(group => ({
        ...group,
        crops: crops.filter(crop => crop.group_id === group.id)
      }));

      return hierarchy;
    } catch (error) {
      throw new Error('Failed to fetch crop hierarchy');
    }
  },

  // Get crops with their groups
  async getCropsWithGroups() {
    try {
      const [cropGroups, crops] = await Promise.all([
        this.getCropGroups(),
        this.getCrops()
      ]);

      return crops.map(crop => ({
        ...crop,
        group: cropGroups.find(group => group.id === crop.group_id)
      }));
    } catch (error) {
      throw new Error('Failed to fetch crops with groups');
    }
  },
};
